import { EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import momentPlugin from '@fullcalendar/moment';
import momentTzPlugin from '@fullcalendar/moment-timezone';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import moment from 'moment-timezone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useGetCalendarEventsByMonthQuery } from 'src/services/calendar/calendar.service';
import { CalendarEventAvailability, ICalendarEvent } from 'src/services/calendar/calendar.types';
import { getEventTypeColor } from 'src/services/calendar/calendar.utils';
import { openCalendarEventViewer } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';

// ----------------------------------------------------------------------

interface CalendarGridViewProps {
  calendarId: string;
  onMonthChange?: (month: number, year: number) => void;
}

export default function CalendarGridView({
  calendarId,
  onMonthChange
}: CalendarGridViewProps) {

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const calendarRef = useRef<FullCalendar>(null);
  const dispatch = useAppDispatch();

  const { account } = useAuthContext();

  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1); // 1-indexed month
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());

  const { currentData: monthlyEvents, isLoading, error } = useGetCalendarEventsByMonthQuery({
    calendarId,
    month: currentMonth,
    year: currentYear,
    timezone: account.timezone || moment.tz.guess()
  }, {
    skip: !calendarId || !currentMonth || !currentYear,
  });

  const formatEvents = useCallback((events: ICalendarEvent[] = []): EventInput[] => events.map((event) => ({
    id: event.id,
    title: event.title,
    start: new Date(event.start_at),
    end: event.end_at || undefined,
    allDay: event.all_day,
    backgroundColor: getEventTypeColor(event.type, theme),
    borderColor: getEventTypeColor(event.type, theme),
    textColor: theme.palette.getContrastText(getEventTypeColor(event.type, theme)),
    extendedProps: { ...event },
  })), [theme]);

  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current?.getApi().removeAllEvents();
      calendarRef.current?.getApi().addEventSource(formatEvents(monthlyEvents || []));
    }
  }, [monthlyEvents, formatEvents]);

  useEffect(() => {
    onMonthChange?.(currentMonth, currentYear);
  }, [currentMonth, currentYear, onMonthChange]);

  const handleDateChange = (dateInfo: any) => {
    const date = new Date(dateInfo.view.currentStart);
    setCurrentMonth(date.getMonth() + 1); // 1-indexed month
    setCurrentYear(date.getFullYear());
  };

  const handleEventClick = (clickInfo: any) => {
    const eventId = clickInfo.event.id;
    dispatch(openCalendarEventViewer({ eventId }));
  };

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {t('calendar.error.loading')}
        </Typography>
        <Typography variant="body2">
          {t('calendar.error.try_again')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: 'calc(100vh - 200px)',
      minHeight: 500,
      bgcolor: 'background.paper',
      borderRadius: 1,
      boxShadow: theme.shadows[1],
      overflow: 'hidden',
    }}>
      <Box sx={{ height: '100%' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, momentPlugin, momentTzPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek',
            }}
            height="100%"
            eventClick={handleEventClick}
            datesSet={handleDateChange}
            dayMaxEvents={isMobile ? 2 : 3}
            timeZone={account.timezone || 'America/New_York'}
            moreLinkClick="popover"
            eventDidMount={(info) => {
              const dotEl = info.el.querySelector('.fc-daygrid-event-dot');
              if (dotEl) {
                if (info.event.extendedProps.availability_type === CalendarEventAvailability.BUSY) {
                  (dotEl as HTMLElement).style.borderColor = theme.palette.error.main;
                }
                else if (info.event.extendedProps.availability_type === CalendarEventAvailability.FREE) {
                  (dotEl as HTMLElement).style.borderColor = theme.palette.success.main;
                }
              }
            }}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short',
            }}
            buttonText={{
              today: t('calendar.today'),
              month: t('calendar.month'),
              week: t('calendar.week'),
              day: t('calendar.day')
            }}
          />
        )}
      </Box>
    </Box>
  );
}